import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Gauge, Sparkles, CheckCircle2, AlertCircle, Loader2, TrendingUp, Wand2, Undo2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { FeatureGate } from './TierComponents';

const API = process.env.REACT_APP_BACKEND_URL;

const scoreColor = (s) => (s >= 75 ? 'text-emerald-600' : s >= 45 ? 'text-amber-600' : 'text-red-600');
const barColor = (s) => (s >= 75 ? 'bg-emerald-500' : s >= 45 ? 'bg-amber-500' : 'bg-red-500');

/**
 * Proactive "documentation strength" meter for the clinical-notes editor. Calls the AI
 * coder/CDI auditor to score how strongly the draft note supports E/M billing and which
 * CPT level it currently supports — so clinicians fix under-documentation BEFORE a claim
 * is created (lifting clean-claim rates). Read-only; no PHI stored.
 */
export const DocStrengthMeter = ({ soap, content, noteType, onApply, canUndo, onUndo }) => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [strengthening, setStrengthening] = useState(false);
  const [result, setResult] = useState(null);

  const analyze = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/clinical-notes/strength`,
        { note_type: noteType, soap, content },
        { ...getAuthHeader(), timeout: 90000 },
      );
      setResult(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setResult({ error: typeof detail === 'string' ? detail : 'Could not analyze documentation' });
    } finally {
      setLoading(false);
    }
  };

  const strengthen = async () => {
    setStrengthening(true);
    try {
      const { data } = await axios.post(
        `${API}/api/clinical-notes/strengthen`,
        { note_type: noteType, soap, content },
        { ...getAuthHeader(), timeout: 120000 },
      );
      onApply?.(data);
      setResult(null); // score is now stale — nudge a re-analyze
    } catch (e) {
      const detail = e.response?.data?.detail;
      setResult({ error: typeof detail === 'string' ? detail : 'Could not strengthen the note' });
    } finally {
      setStrengthening(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-cn-border bg-cn-bg/50 p-4" data-testid="doc-strength-meter">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-cn-text">
          <Gauge className="w-4 h-4 text-cn-primary" /> Documentation strength
        </div>
        <button
          type="button"
          onClick={analyze}
          disabled={loading}
          className="cn-btn cn-btn-ghost !py-1.5 !px-3 text-xs"
          data-testid="doc-strength-analyze-btn"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {result && !result.error ? 'Re-analyze' : 'Analyze with AI'}
        </button>
      </div>
      <p className="mt-1 text-xs text-cn-text-muted">
        See how strongly your note supports billing — before you create a claim.
      </p>

      <FeatureGate
        feature="ai_soap"
        fallback={
          <>
            <Link
              to="/pricing"
              className="cn-btn cn-btn-ghost !py-2 w-full justify-center mt-3 text-sm border border-dashed border-cn-primary/40 text-cn-primary hover:bg-cn-primary/5"
              data-testid="doc-strengthen-locked-link"
            >
              <Sparkles className="w-4 h-4" />
              AI Strengthen · Enterprise feature
            </Link>
            <p className="mt-1.5 text-[10px] text-cn-text-muted">
              Unlock AI SOAP drafting + auto-strengthen on the Enterprise tier ($18 PMPM bundle).
            </p>
          </>
        }
      >
        <button
          type="button"
          onClick={strengthen}
          disabled={strengthening || loading}
          className="cn-btn cn-btn-primary !py-2 w-full justify-center mt-3 text-sm"
          data-testid="doc-strengthen-btn"
        >
          {strengthening ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          {strengthening ? 'Strengthening…' : 'Strengthen this note with AI'}
        </button>
        <p className="mt-1.5 text-[10px] text-cn-text-muted">
          AI restructures your draft and adds <span className="font-mono">[ADD: …]</span> prompts for missing details. It never invents clinical facts — review before saving.
        </p>
      </FeatureGate>

      {canUndo && (
        <button
          type="button"
          onClick={onUndo}
          className="cn-btn cn-btn-ghost !py-1.5 w-full justify-center mt-2 text-xs"
          data-testid="doc-strengthen-undo-btn"
        >
          <Undo2 className="w-3.5 h-3.5" /> Undo AI changes
        </button>
      )}

      {result && !result.error && (
        <div className="mt-3" data-testid="doc-strength-result">
          <div className="flex items-end justify-between">
            <div className={`font-display font-bold text-3xl ${scoreColor(result.score)}`} data-testid="doc-strength-score">
              {result.score}
              <span className="text-base text-cn-text-muted font-normal">/100</span>
            </div>
            {result.highest_supported_cpt && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Supports up to</div>
                <div className="font-mono font-semibold text-cn-primary text-sm" data-testid="doc-strength-cpt">{result.highest_supported_cpt}</div>
              </div>
            )}
          </div>
          <div className="mt-2 h-2.5 rounded-full bg-cn-border/40 overflow-hidden">
            <div className={`h-full rounded-full ${barColor(result.score)} transition-all duration-500`} style={{ width: `${result.score}%` }} />
          </div>
          {result.level_label && <p className="mt-2 text-sm font-medium text-cn-text" data-testid="doc-strength-label">{result.level_label}</p>}

          {(result.strengths || []).length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {result.strengths.map((s, i) => (
                <li key={i} className="flex items-start gap-1.5 text-xs text-emerald-700" data-testid={`doc-strength-strength-${i}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />{s}
                </li>
              ))}
            </ul>
          )}
          {(result.suggestions || []).length > 0 && (
            <div className="mt-2">
              <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-cn-primary" /> Strengthen your note
              </p>
              <ul className="mt-1 space-y-0.5">
                {result.suggestions.map((s, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-cn-text" data-testid={`doc-strength-suggestion-${i}`}>
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-cn-primary shrink-0" />{s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.ai_unavailable && <p className="mt-2 text-[10px] text-cn-text-muted">AI was momentarily unavailable — try again.</p>}
        </div>
      )}
      {result?.error && (
        <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5" data-testid="doc-strength-error">
          <AlertCircle className="w-3.5 h-3.5" />{typeof result.error === 'string' ? result.error : 'Analysis failed'}
        </p>
      )}
    </div>
  );
};

export default DocStrengthMeter;
